export const SEARCH_SUGGESTION_CATEGORY_OPTIONS = [
  "Women's",
  "Men's",
  "Both (M+W)",
  "Kids",
  "Beauty",
] as const;

export type SearchSuggestionCategory = (typeof SEARCH_SUGGESTION_CATEGORY_OPTIONS)[number];

function isSearchSuggestionCategory(value: string): value is SearchSuggestionCategory {
  return SEARCH_SUGGESTION_CATEGORY_OPTIONS.includes(value as SearchSuggestionCategory);
}

function getActiveSuggestions(
  suggestionLibrary: Record<SearchSuggestionCategory, string[]>,
  activeIndices: Record<SearchSuggestionCategory, number[]>,
  category: SearchSuggestionCategory
): string[] {
  const suggestions = suggestionLibrary[category] ?? [];
  const indices = activeIndices[category] ?? [];

  return indices
    .map((index) => suggestions[index])
    .filter((suggestion): suggestion is string => Boolean(suggestion));
}

// Saved search suggestion options for each category.
export const SEARCH_SUGGESTION_LIBRARY_BY_CATEGORY: Record<SearchSuggestionCategory, string[]> = {
  "Women's": [
    "Dress for winter wedding",
    "High quality sweater under $250",
    "Midi skirt in wool",
    "Poplin shirtdress for spring",
    "Soft blouse and relaxed trousers",
    "Leather loafers or mules for everyday",
    "Cotton poplin shirtdress for work",
    "Everyday leather loafers",
    "Sunglasses similar to Gentle Monster under $250",
  ],
  "Men's": [
    "Wool coat like COS or Toteme",
    "High quality sweater under $250",
    "Common Projects-style sneakers under $300",
    "Lightweight spring jacket",
    "Linen trousers under $250",
    "Striped shirt with relaxed trousers",
    "Everyday lightweight spring jacket",
    "Minimal sneakers like Common Projects",
    "Suede sneakers under $300",
  ],
  "Both (M+W)": [
    "Cashmere beanie from a cool independent brand",
    "High quality sweater under $250",
    "Structured tote bag for the office",
    "Minimal sunglasses for spring",
    "Lightweight jacket from an independent brand",
    "Everyday leather tote for work",
    "Black leather belt for everyday",
    "Weekend bag under $300",
    "Everyday sneakers like Common Projects",
  ],
  Kids: [
    "Warm hat and mittens set",
    "Puffer jacket in playful colors",
    "Pajamas in organic cotton",
    "Rain boots & puddle-ready shoes",
    "Windbreakers & light layers",
    "Everyday sneakers for kids",
    "Waterproof rain boots",
    "Lightweight rain jackets",
    "Spring sneakers for kids",
  ],
  Beauty: [
    "Winter skincare routine essentials",
    "Hydrating lip balm with tint",
    "Clean fragrance under $100",
    "Skin perfume for spring",
    "Bronzing drops for spring",
    "Tinted moisturizer with glow",
    "Skin tint with natural finish",
    "Clean skin perfume for spring",
    "Cream blush",
  ],
};

// Change these indices to choose which saved suggestions appear in the hero dropdown.
export const ACTIVE_SEARCH_SUGGESTION_INDICES_BY_CATEGORY: Record<
  SearchSuggestionCategory,
  number[]
> = {
  "Women's": [6, 7, 8],
  "Men's": [6, 7, 4],
  "Both (M+W)": [3, 5, 8],
  Kids: [6, 7, 8],
  Beauty: [6, 7, 8],
};

export function getActiveSearchSuggestions(category: string): string[] {
  const normalizedCategory = isSearchSuggestionCategory(category) ? category : "Women's";

  return getActiveSuggestions(
    SEARCH_SUGGESTION_LIBRARY_BY_CATEGORY,
    ACTIVE_SEARCH_SUGGESTION_INDICES_BY_CATEGORY,
    normalizedCategory
  );
}
